import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule
} from "@angular/core";
import {
  DiaprofilPatient,
  diaprofilPatientFactory
} from "./models/diaprofil-patient";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { IonicApp, IonicModule } from "ionic-angular";
import { ManualItem, manualItemFactory } from "./models/manual-item";
import {
  QuestionnaireModule,
  QuestionnaireResponseService
} from "@zitelab/ionic-fhir-questionnaires";
// Import of ngrx/store
import { Store, StoreModule } from "@ngrx/store";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { filter, first, tap } from "rxjs/operators";

import { AdLoginPage } from "../pages/ad-login.page/ad-login.page";
import { AdminService } from "./services/admin.service";
import { AllAnswersModal } from "../modals/all-answers.modal/all-answers.modal";
import { AppState } from "./models/app.state";
import { AuthInterceptor } from "./services/auth.interceptor";
import { AuthService } from "./services/auth.service";
import { AutoLogoutService } from "./services/auto-logout.service";
import { BooleanToYesNoPipe } from "./pipes/boolean-to-yes-no.pipe";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BrowserModule } from "@angular/platform-browser";
import { ChangePasswordModal } from "../modals/change-password.modal/change-password.modal";
import { ClinicalSuitePage } from "../pages/clinical-suite.page/clinical-suite.page";
import { ConsultationStatusModal } from "../modals/consultation-status.modal/consultation-status.modal";
import { CurrentConsultationsPage } from "../pages/current-consultations.page/current-consultations.page";
import { CustomErrorHandler } from "./services/errorhandler";
import { DashboardPage } from "../pages/dashboard.page/dashboard.page";
import { DashboardService } from "./services/dashboard.service";
import { DataExtractPage } from "../pages/data-extract.page/data-extract.page";
import { DatePipe } from "@angular/common";
import { DiaProfilService } from "./services/diaprofil.service";
import { DiabetesApp } from "./app.component";
import { DialogueInputCard } from "../pages/dashboard.page/dialogue-input-card.component/dialogue-input-card";
import { DiaprofilChart } from "../components/diaprofil-chart.component/diaprofil-chart.component";
import { DomainNamePipe } from "./pipes/domain-name.pipe";
import { EffectsModule } from "@ngrx/effects";
import { EnvironmentsModule } from "./environment-variables/environment-variables.module";
import { ErrorInterceptor } from "./services/error.interceptor";
import { EventLoggingService } from "./services/event-logging.service";
import { FhirService } from "./services/fhir.service";
import { FrontPage } from "../pages/front.page/front.page";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { HcpPage } from "../pages/hcp.page/hcp.page";
import { HcpUsersPage } from "../pages/hcp-users.page/hcp-users.page";
import { HomePage } from "../pages/home.page/home.page";
import { IonicStorageModule } from "@ionic/storage";
import { KihImportModal } from "../modals/kih-import.modal/kih-import.modal";
import { KramModal } from "../modals/kram.modal/kram.modal";
import { LogEventListingPage } from "../pages/log-event-listing.page/log-event-listing.page";
import { LoginPage } from "../pages/login.page/login.page";
import { ManageHcpUserModal } from "../modals/manage-hcp-user.modal/manage-hcp-user.modal";
import { ManageManualItemModal } from "../modals/manage-manual-item.modal/manage-manual-item.modal";
import { ManagePatientModal } from "../modals/manage-patient.modal/manage-patient.modal";
import { ManageSupportActionModal } from "../modals/manage-support-action.modal/manage-support-action.modal";
import { ManageTaskModal } from "../modals/manage-task.modal/manage-task.modal";
import { ManualAdminPage } from "../pages/manual-admin.page/manual-admin.page";
import { ManualItemsService } from "./services/manual-items.service";
import { MarkdownModule } from "angular2-markdown";
// Import of other third party plugins
import { MomentModule } from "angular2-moment";
import { Ng2FlatpickrModule } from "ng2-flatpickr";
import { NgSelectModule } from "@ng-select/ng-select";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { NotificationLogModal } from "../modals/notification-log.modal/notification-log.modal";
import { NotificationLogService } from "./services/notification-log.service";
import { PageHeader } from "../components/page-header.component/page-header.component";
import { PatchProModal } from "../modals/patch-pro.modal/patch-pro.modal";
import { PatientChartsModal } from "../modals/patient-charts.modal/patient-charts.modal";
import { PatientListingPage } from "../pages/patient-listing.page/patient-listing.page";
import { PatientPage } from "../pages/patient.page/patient.page";
import { PloneDownloadDirective } from "./directives/plone-download.directve";
import { PrivacyPolicyPage } from "../pages/privacy-policy.page/privacy-policy.page";
import { ProAnswersTable } from "../components/pro-answers-table.component/pro-answers-table.component";
import { ProTaskCard } from "../pages/patient.page/pro-task-card.component/pro-task-card.component";
import { QuestionnaireEffects } from "./store/effects/questionnaire.effects";
import { QuillModule } from "ngx-quill";
import Raven from "raven-js";
import { ReadableCprPipe } from "./pipes/readable-cpr.pipe";
import { RelevantSupportActionsModal } from "../modals/relevant-support-actions.modal/relevant-support-actions.modal";
import { SelectPatientListingColumns } from "../pages/patient-listing.page/select-patient-listing-columns.component/select-patient-listing-columns.component";
import { SettingsPage } from "../pages/settings.page/settings.page";
import { SplashScreen } from "@ionic-native/splash-screen";
import { StatisticsPage } from "../pages/statistics.page/statistics.page";
import { StatusBar } from "@ionic-native/status-bar";
import { StorageSyncEffects } from "ngrx-store-ionic-storage/dist";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { SupportActionsAdminPage } from "../pages/support-actions-admin.page/support-actions-admin.page";
import { SupportActionsService } from "./services/support-actions.service";
import { TaskLogModal } from "../modals/task-log.modal/task-log.modal";
import { TooltipsModule } from "ionic-tooltips";
import { TopicAnswersModal } from "../pages/dashboard.page/topic-card.component/topic-answers.modal/topic-answers.modal";
import { TopicCard } from "../pages/dashboard.page/topic-card.component/topic-card.component";
import { TopicCardKeyItemSection } from "../pages/dashboard.page/topic-card.component/topic-card-key-item-section.component/topic-card-key-item-section.component";
import { TopicCardPopover } from "../pages/dashboard.page/topic-card.component/topic-card.popover/topic-card.popover";
import { TopicEvaluationForm } from "../pages/dashboard.page/topic-card.component/topic-evaluation-form.component/topic-evaluation-form.component";
import { TopicInfoModal } from "../pages/dashboard.page/topic-card.component/topic-info.modal/topic-info.modal";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateOptionsArray } from "./pipes/translate-options-array";
import { TranslateService } from "@ngx-translate/core";
import { TwoFactorPinPage } from "../pages/two-factor-pin.page/two-factor-pin.page";
import { UserEffects } from "./store/effects/user.effects";
import { UserGuidePage } from "../pages/user-guide.page/user-guide.page";
import { WINDOW_PROVIDERS } from "./services/window.service";
import { Who5 } from "./pipes/who5-pipe";
import { metaReducers } from "./store/reducers/storage-sync.reducer";
import { reducers } from "./store/reducers/root.reducer";

Raven.config("https://534dfa560adc4b31a8e9334a202bedc4@sentry.zitelab.eu/25", {
  /** The environment of the application you are monitoring with Sentry */
  environment: process.env.ENV,
  /** The release version of the application you are monitoring with Sentry */
  release: process.env.VERSION_NUMBER
});

function initializeAppFactory(store: Store<AppState>): () => Promise<boolean> {
  return () =>
    store
      .select(state => state.hydrated)
      .pipe(
        filter(hydrated => hydrated === true),
        tap(hydrated => console.log("APP STATE HYDRATION", hydrated)),
        first()
      )
      .toPromise();
}

const pages = [
  DiabetesApp,
  FrontPage,
  PatientListingPage,
  DashboardPage,
  LoginPage,
  SupportActionsAdminPage,
  ManualAdminPage,
  PrivacyPolicyPage,
  UserGuidePage,
  TwoFactorPinPage,
  HcpUsersPage,
  HcpPage,
  PatientPage,
  HomePage,
  SettingsPage,
  StatisticsPage,
  DataExtractPage,
  ClinicalSuitePage,
  AdLoginPage,
  CurrentConsultationsPage,
  LogEventListingPage
];

const modals = [
  RelevantSupportActionsModal,
  ManageTaskModal,
  ManageSupportActionModal,
  ManageManualItemModal,
  ManagePatientModal,
  ManageHcpUserModal,
  ChangePasswordModal,
  PatchProModal,
  KihImportModal,
  PatientChartsModal,
  TaskLogModal,
  NotificationLogModal,
  KramModal,
  AllAnswersModal,
  ConsultationStatusModal
];

const components = [
  TopicCard,
  TopicCardKeyItemSection,
  TopicCardPopover,
  TopicInfoModal,
  TopicAnswersModal,
  TopicEvaluationForm,
  DialogueInputCard,
  PageHeader,
  SelectPatientListingColumns,
  ProTaskCard,
  DiaprofilChart,
  ProAnswersTable
];
const pipes = [
  ReadableCprPipe,
  DomainNamePipe,
  BooleanToYesNoPipe,
  TranslateOptionsArray,
  Who5
];
const directives = [PloneDownloadDirective];
@NgModule({
  declarations: [...pages, ...modals, ...components, ...pipes, ...directives],
  imports: [
    EnvironmentsModule,
    QuestionnaireModule.forRoot({
      questionnaireStoreKey: "questionnaires",
      ifqSettingsKey: "ifqSettings",
      fhirStoreKey: "fhir",
      useDefaultTranslations: true,
      enableDebugMode: true
    }),
    HttpClientModule,
    MomentModule,
    QuillModule,
    NgxDatatableModule,
    NgSelectModule,
    Ng2FlatpickrModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    BrowserModule,
    IonicModule.forRoot(
      DiabetesApp,
      { menuType: "overlay", mode: "md" },
      {
        links: [
          {
            component: ClinicalSuitePage,
            name: "clinical-suite",
            segment: "clinical-suite/:params"
          },
          {
            component: AdLoginPage,
            name: "ad-login",
            segment: "ad-login/:payload"
          }
        ]
      }
    ),
    IonicStorageModule.forRoot(),
    MarkdownModule.forRoot(),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([
      StorageSyncEffects,
      QuestionnaireEffects,
      UserEffects
    ]),
    TooltipsModule.forRoot(),
    BrowserAnimationsModule,
    // Note that you must instrument ngrx/store dev tools after importing StoreModule
    process.env.ENABLE_DEBUGGING === "YES"
      ? StoreDevtoolsModule.instrument({ maxAge: 500 })
      : []
  ],
  bootstrap: [IonicApp],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  entryComponents: [...pages, ...modals, ...components],
  providers: [
    StatusBar,
    SplashScreen,
    DatePipe,
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    AuthService,
    FhirService,
    DashboardService,
    DiaProfilService,
    AdminService,
    SupportActionsService,
    ManualItemsService,
    AutoLogoutService,
    EventLoggingService,
    WINDOW_PROVIDERS,
    NotificationLogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: DiaprofilPatient,
      useFactory: diaprofilPatientFactory,
      deps: [TranslateService, QuestionnaireResponseService]
    },
    {
      provide: ManualItem,
      useFactory: manualItemFactory,
      deps: [TranslateService]
    },
    {
      provide: APP_INITIALIZER,
      deps: [Store],
      useFactory: store => initializeAppFactory(store),
      multi: true
    }
  ]
})
export class AppModule {}

// AoT requires an exported function for factories
// By default this will look for your translation json files in i18n/, but for Ionic you must change this to look in the src/assets directory. We can do this by creating a function that returns a new TranslateLoader:
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts